import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Radio, Upload } from 'antd';
import { ArrowLeftOutlined, UploadOutlined, PlayCircleOutlined } from '@ant-design/icons';
import type { RadioChangeEvent, UploadProps } from 'antd';

import { useResponsive } from '../../hooks/useResponsive';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { PageContainer } from '../../components/layouts/PageLayouts/PageContainer/PageContainer';
import { ContentCard } from '../../components/layouts/PageLayouts/ContentCard/ContentCard';
import { BusinessButton } from '../../components/common/buttons/BusinessButton/BusinessButton';
import { notificationController } from '../../controllers/notificationController';
import { IAssessmentsState } from '../../store/reducers/assessments/types';
import { IClassesState } from '../../store/reducers/classes/types';

// Styled Components
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const BackButton = styled(BusinessButton)`
  flex-shrink: 0;
`;

const TitleSection = styled.div`
  flex: 1;
  
  h1 {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 12px;
    
    .icon {
      font-size: 32px;
    }
  }
  
  .subtitle {
    font-size: 14px;
    color: #666;
    font-weight: 500;
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 22px;
      
      .icon {
        font-size: 26px;
      }
    }
    
    .subtitle {
      font-size: 13px;
    }
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr 380px;
  gap: 24px;
  
  @media (max-width: 1400px) {
    grid-template-columns: 280px 1fr;
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const StudentsPanel = styled(ContentCard)`
  height: fit-content;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
`;

const StudentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StudentItem = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  
  ${props => props.selected ? `
    background: rgba(22, 119, 255, 0.08);
    border-color: rgba(22, 119, 255, 0.3);
  ` : `
    background: rgba(0, 0, 0, 0.02);
    
    &:hover {
      background: rgba(0, 0, 0, 0.04);
      border-color: rgba(0, 0, 0, 0.1);
    }
  `}
  
  .status-icon {
    font-size: 20px;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &.approved {
      color: #52c41a;
    }
    
    &.pending {
      color: #faad14;
    }
  }
  
  .student-info {
    flex: 1;
    min-width: 0;
    
    .name {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .score {
      font-size: 12px;
      color: #666;
      font-weight: 500;
    }
  }
  
  .status-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    
    &.approved {
      color: #52c41a;
    }
    
    &.pending {
      color: #faad14;
    }
  }
`;

const GradingPanel = styled(ContentCard)``;

const ModeSelector = styled.div`
  margin-bottom: 32px;
  
  .mode-label {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 12px;
    display: block;
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  gap: 12px;
  
  .ant-radio-button-wrapper {
    height: 44px;
    line-height: 42px;
    padding: 0 24px;
    border-radius: 8px;
    font-weight: 500;
    
    &.ant-radio-button-wrapper-checked {
      background: #1677ff;
      border-color: #1677ff;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    
    .ant-radio-button-wrapper {
      text-align: center;
    }
  }
`;

const ModeContent = styled.div`
  padding: 24px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
`;

const AutomaticModeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const UploadSection = styled.div`
  .upload-label {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 12px;
    display: block;
  }
  
  .upload-hint {
    font-size: 12px;
    color: #666;
    margin-top: 8px;
  }
`;

const StyledUpload = styled(Upload)`
  .ant-upload {
    width: 100%;
  }
`;

const ActionButton = styled(BusinessButton)`
  width: 100%;
  margin-top: 12px;
`;

const ManualModeSection = styled.div`
  text-align: center;
  padding: 40px 20px;
  
  .icon {
    font-size: 64px;
    margin-bottom: 16px;
    display: block;
  }
  
  .title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 8px;
  }
  
  .description {
    font-size: 14px;
    color: #666;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(0, 0, 0, 0.45);
  
  .icon {
    font-size: 48px;
    margin-bottom: 12px;
    display: block;
  }
  
  .title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 6px;
    color: rgba(0, 0, 0, 0.65);
  }
  
  .description {
    font-size: 13px;
  }
`;

// Answer Sheet Viewer Components
const AnswerSheetViewer = styled(ContentCard)`
  height: fit-content;
  
  @media (max-width: 1400px) {
    grid-column: span 2;
  }
  
  @media (max-width: 1024px) {
    grid-column: span 1;
  }
`;

const AnswerSheetContainer = styled.div`
  width: 100%;
  background: #f5f5f5;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 600px;
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

// Manual Grading Panel Components
const ManualGradingPanel = styled(ContentCard)`
  height: fit-content;
  
  @media (max-width: 1400px) {
    display: none;
  }
`;

const GradingScaleSection = styled.div`
  margin-bottom: 24px;
  
  .scale-title {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 12px;
    display: block;
  }
`;

const GradeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  margin-bottom: 8px;
  
  .grade-letter {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a1a;
  }
  
  .grade-range {
    font-size: 14px;
    color: #666;
    font-weight: 500;
  }
`;

const ScoreInputSection = styled.div`
  margin-bottom: 24px;
  
  .input-label {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 12px;
    display: block;
  }
`;

const ScoreInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 2px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #1677ff;
    box-shadow: 0 0 0 3px rgba(22, 119, 255, 0.1);
  }
  
  &:disabled {
    background: rgba(0, 0, 0, 0.04);
    cursor: not-allowed;
  }
`;

const GradeResultSection = styled.div`
  margin-bottom: 24px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(22, 119, 255, 0.08) 0%, rgba(82, 196, 26, 0.08) 100%);
  border-radius: 12px;
  text-align: center;
  
  .grade-label {
    font-size: 13px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
  }
  
  .grade-value {
    font-size: 48px;
    font-weight: 700;
    color: #1677ff;
    line-height: 1;
  }
`;

const ApproveButton = styled(BusinessButton)`
  width: 100%;
`;

const GradingAssessmentPage: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get assessment ID from URL
  const assessmentId = searchParams.get('assessmentId');
  
  // Redux
  const { assessments } = useTypedSelector<IAssessmentsState>(state => state.assessments);
  const { classes } = useTypedSelector<IClassesState>(state => state.classes);
  const { getAssessmentsRequest, getClassesRequest } = useActions();
  
  // Local state
  const [gradingMode, setGradingMode] = useState<'manual' | 'automatic'>('manual');
  const [selectedStudent, setSelectedStudent] = useState<number | null>(null);
  const [answerKeyFile, setAnswerKeyFile] = useState<any>(null);
  const [finalScore, setFinalScore] = useState<string>('');
  
  // Grading Scale (mock data - replace with real data from assessment)
  const gradingScale = [
    { grade: 'A', min: 45, max: 50 },
    { grade: 'B', min: 38, max: 44 },
    { grade: 'C', min: 30, max: 37 },
    { grade: 'D', min: 0, max: 29 },
  ];
  
  // Mock students data (replace with real data later)
  // status: 'approved' | 'pending' | 'not_uploaded'
  const [students] = useState([
    { id: 1, name: 'Alice Johnson', score: 42, total: 50, status: 'approved' },
    { id: 2, name: 'Bob Smith', score: null, total: 50, status: 'pending' },
    { id: 3, name: 'Clara Lee', score: null, total: 50, status: 'pending' },
    { id: 4, name: 'David Chen', score: null, total: 50, status: 'not_uploaded' },
    { id: 5, name: 'Emma Wilson', score: 38, total: 50, status: 'approved' },
  ]);
  
  // Find current assessment
  const assessment: any = assessments?.find((a: any) => a.id === Number(assessmentId));
  const assessmentName = assessment?.name || assessment?.examName || 'Assessment';
  const totalStudents = students.length;
  const totalQuestions = assessment?.questions || 50;

  useEffect(() => {
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId && storedUserId !== 'null') {
      getAssessmentsRequest();
      getClassesRequest();
    }
  }, []);

  const handleBack = () => {
    navigate('/grading');
  };

  const handleModeChange = (e: RadioChangeEvent) => {
    setGradingMode(e.target.value);
  };

  const handleStudentClick = (studentId: number) => {
    setSelectedStudent(studentId);
    // Load student's score if already graded
    const student = students.find(s => s.id === studentId);
    if (student && student.score !== null) {
      setFinalScore(student.score.toString());
    } else {
      setFinalScore('');
    }
  };
  
  const calculateGrade = (score: number): string => {
    for (const scale of gradingScale) {
      if (score >= scale.min && score <= scale.max) {
        return scale.grade;
      }
    }
    return '—';
  };
  
  const handleFinalScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value);
      // Check if within range
      if (value === '' || (numValue >= 0 && numValue <= totalQuestions)) {
        setFinalScore(value);
      }
    }
  };
  
  const handleApproveStudent = () => {
    if (!selectedStudent) {
      notificationController.error({
        message: 'No Student Selected',
        description: 'Please select a student first.'
      });
      return;
    }
    
    if (!finalScore || finalScore === '') {
      notificationController.error({
        message: 'No Score Entered',
        description: 'Please enter a final score first.'
      });
      return;
    }
    
    const score = parseInt(finalScore);
    const grade = calculateGrade(score);
    const student = students.find(s => s.id === selectedStudent);
    
    // TODO: Send to API to update student status to 'approved'
    notificationController.success({
      message: 'Student Approved',
      description: `${student?.name} has been approved with score ${score}/${totalQuestions} (Grade: ${grade})`
    });
    
    // Update student status locally (this should be done via API)
    // For now, just show success message
  };

  const uploadProps: UploadProps = {
    accept: '.csv',
    maxCount: 1,
    beforeUpload: (file) => {
      setAnswerKeyFile(file);
      notificationController.success({
        message: 'Answer Key Uploaded',
        description: `File "${file.name}" uploaded successfully.`
      });
      return false;
    },
    onRemove: () => {
      setAnswerKeyFile(null);
    },
  };

  const handleRunAutoGrading = () => {
    if (!answerKeyFile) {
      notificationController.error({
        message: 'No Answer Key',
        description: 'Please upload an answer key CSV file first.'
      });
      return;
    }

    notificationController.info({
      message: 'Running Automatic Grading',
      description: 'Processing answer sheets... This may take a few moments.'
    });

    // TODO: Implement automatic grading logic
  };

  return (
    <>
      <PageTitle 
        title={`Grading - ${assessmentName}`}
        description="Grade assessment answer sheets" 
        url={window.location.href} 
        noIndex={true}
      />
      
      <PageContainer>
        <PageWrapper>
          {/* Header */}
          <HeaderSection>
            <BackButton
              variant="ghost"
              businessSize="small"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            >
              Back to Grading
            </BackButton>
          </HeaderSection>
          
          <TitleSection>
            <h1>
              <span className="icon">🧾</span>
              Grading – {assessmentName}
            </h1>
            <div className="subtitle">
              👥 {totalStudents} students • ❓ {totalQuestions} questions
            </div>
          </TitleSection>

          {/* Main Content */}
          <MainContent>
            {/* Students Panel */}
            <StudentsPanel
              title="Students"
              subtitle={`${students.filter((s: any) => s.status === 'approved').length} / ${totalStudents} approved`}
            >
              {students.length > 0 ? (
                <StudentsList>
                  {students.map((student: any) => {
                    const getStatusIcon = () => {
                      if (student.status === 'approved') return '✔';
                      if (student.status === 'pending') return '◔';
                      return null;
                    };
                    
                    const getStatusLabel = () => {
                      if (student.status === 'approved') return 'Approved';
                      if (student.status === 'pending') return 'Pending';
                      return null;
                    };
                    
                    return (
                      <StudentItem 
                        key={student.id}
                        selected={selectedStudent === student.id}
                        onClick={() => handleStudentClick(student.id)}
                      >
                        {getStatusIcon() && (
                          <div className={`status-icon ${student.status}`}>
                            {getStatusIcon()}
                          </div>
                        )}
                        
                        <div className="student-info">
                          <div className="name">{student.name}</div>
                          <div className="score">
                            {student.score !== null ? `${student.score}/${student.total}` : '—'}
                          </div>
                        </div>
                        
                        {getStatusLabel() && (
                          <div className={`status-label ${student.status}`}>
                            {getStatusLabel()}
                          </div>
                        )}
                      </StudentItem>
                    );
                  })}
                </StudentsList>
              ) : (
                <EmptyState>
                  <span className="icon">👥</span>
                  <div className="title">No Students</div>
                  <div className="description">No students found for this assessment</div>
                </EmptyState>
              )}
            </StudentsPanel>

            {/* Answer Sheet Viewer */}
            <AnswerSheetViewer
              title="Answer Sheet"
              subtitle={selectedStudent ? students.find((s: any) => s.id === selectedStudent)?.name : 'Select a student to view their answer sheet'}
            >
              {selectedStudent ? (
                <AnswerSheetContainer>
                  {/* TODO: Replace with actual answer sheet image */}
                  <div style={{ textAlign: 'center', color: '#999' }}>
                    <div style={{ fontSize: 64, marginBottom: 16 }}>📄</div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                      Answer Sheet Preview
                    </div>
                    <div style={{ fontSize: 14 }}>
                      Student: {students.find((s: any) => s.id === selectedStudent)?.name}
                    </div>
                  </div>
                </AnswerSheetContainer>
              ) : (
                <EmptyState>
                  <span className="icon">📋</span>
                  <div className="title">No Student Selected</div>
                  <div className="description">Select a student from the list to view their answer sheet</div>
                </EmptyState>
              )}
            </AnswerSheetViewer>

            {/* Manual Grading Panel (only in manual mode) */}
            {gradingMode === 'manual' && selectedStudent && (
              <ManualGradingPanel
                title="Manual Grading"
                subtitle="Enter score and approve"
              >
                <GradingScaleSection>
                  <label className="scale-title">Grading Scale:</label>
                  {gradingScale.map((scale) => (
                    <GradeItem key={scale.grade}>
                      <span className="grade-letter">{scale.grade}:</span>
                      <span className="grade-range">{scale.min}–{scale.max}</span>
                    </GradeItem>
                  ))}
                </GradingScaleSection>

                <ScoreInputSection>
                  <label className="input-label">Final Score:</label>
                  <ScoreInput
                    type="text"
                    placeholder="0"
                    value={finalScore}
                    onChange={handleFinalScoreChange}
                    max={totalQuestions}
                  />
                </ScoreInputSection>

                {finalScore && (
                  <GradeResultSection>
                    <div className="grade-label">Grade</div>
                    <div className="grade-value">{calculateGrade(parseInt(finalScore))}</div>
                  </GradeResultSection>
                )}

                <ApproveButton
                  variant="primary"
                  businessSize="large"
                  onClick={handleApproveStudent}
                  disabled={!finalScore}
                >
                  Approve Student
                </ApproveButton>
              </ManualGradingPanel>
            )}
          </MainContent>
        </PageWrapper>
      </PageContainer>
    </>
  );
};

export default GradingAssessmentPage;

